<template>
  <div ref="anchor" class="floating-lyrics-anchor">
    <section v-show="floating.open" ref="panel" class="floating-lyrics" :class="{ external: floating.external, 'salt-floating': workspace.appearance === 'salt', 'apple-floating': workspace.appearance === 'apple' }" :data-floating-material="workspace.material" :style="panelStyle" role="region" aria-label="悬浮歌词" @keydown.capture="keydown" @pointerdown="startDrag" @pointermove="drag" @pointerup="endDrag" @pointercancel="endDrag">
      <SaltMaterialSurface v-if="compactAppearance" />
      <header class="floating-heading">
        <div class="floating-actions">
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" aria-label="减小悬浮歌词字号" :disabled="floating.fontSize <= 18" @click="font(-2)">A−</component>
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" aria-label="增大悬浮歌词字号" :disabled="floating.fontSize >= 44" @click="font(2)">A＋</component>
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" aria-label="悬浮歌词翻译" :aria-pressed="String(floating.translation)" @click="translation">译</component>
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" :aria-label="floating.locked ? '解锁悬浮歌词位置' : '锁定悬浮歌词位置'" :aria-pressed="String(floating.locked)" :title="floating.external ? '返回页面后保持此位置锁定偏好；独立窗口由浏览器管理' : '锁定后歌词位置不会被拖动'" @click="toggleLock">{{ floating.locked ? '解锁' : '锁定' }}</component>
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" v-if="floating.external" type="button" aria-label="歌词返回页面" @click="returnToPage">↙</component>
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" v-else type="button" aria-label="置顶歌词窗口" :disabled="!pipSupported || pinPending" :title="pipSupported ? '独立置顶窗口，可拖动窗口标题栏' : '当前浏览器不支持独立置顶窗口，仍可拖动页内歌词'" @click="pin">↗</component>
          <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" aria-label="关闭悬浮歌词" @click="close">×</component>
        </div>
      </header>
      <div class="floating-lines" :class="{ 'plain-lines': plainLyrics }" aria-live="off">
        <p class="floating-current" :class="{ 'is-plain': plainLyrics }" :style="{ '--lyric-fill': fill + '%' }">{{ currentText }}</p>
        <p v-if="floating.translation && currentLine && currentLine.translatedLyric" class="floating-translation">{{ currentLine.translatedLyric }}</p>
        <p v-else-if="nextText" class="floating-next">{{ nextText }}</p>
      </div>
      <footer class="floating-footer">
        <span class="floating-title">{{ track ? track.title + ' · ' + track.artists.join(' / ') : '选择一首歌曲' }}</span>
        <span>{{ formatTime(playhead) }}</span>
        <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" aria-label="悬浮歌词上一首" :disabled="!track" @click="runtime.nextTrack(-1)">‹</component>
        <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" :aria-label="state.isPlaying ? '悬浮歌词暂停' : '悬浮歌词播放'" :disabled="!track" @click="runtime.togglePlay()">{{ state.isPlaying ? 'Ⅱ' : '▶' }}</component>
        <component :is="compactAppearance ? 'SaltButton' : 'button'" v-bind="floatingButtonProps" type="button" aria-label="悬浮歌词下一首" :disabled="!track" @click="runtime.nextTrack(1)">›</component>
      </footer>
      <p v-if="error" class="floating-error" role="status">{{ error }}</p>
    </section>
  </div>
</template>

<script>
import { runtime } from '../bridge';
import { floatingLyrics, saveFloatingLyrics } from '../floating-lyrics';
import { playbackSpace } from '../playback-keyboard';
import { workspace } from '../workspace';
import SaltButton from '../salt/ui/SaltButton.vue';
import SaltMaterialSurface from '../salt/ui/SaltMaterialSurface.vue';

export default {
  name: 'FloatingLyrics', components: { SaltButton, SaltMaterialSurface },
  data() { return { runtime, workspace, state: runtime.state, floating: floatingLyrics, playhead: runtime.currentPlaybackTime(), pinPending: false, error: '', pipSupported: Boolean(window.documentPictureInPicture?.requestWindow) }; },
  computed: {
    compactAppearance() { return ['salt', 'apple'].includes(this.workspace.appearance); },
    floatingButtonProps() { return this.compactAppearance ? { styleType: 'plain', iconOnly: true } : {}; },
    track() { return this.state.tracks.find(track => track.id === this.state.currentId); },
    lines() { return this.track ? this.runtime.effectiveAmllLyrics(this.track) : []; },
    lineIndex() {
      const now = this.playhead * 1000;
      let index = -1;
      for (let cursor = 0; cursor < this.lines.length; cursor++) { if (this.lines[cursor].startTime <= now) index = cursor; else break; }
      return index;
    },
    currentLine() { return this.lines[this.lineIndex]; },
    plainLyrics() { return !this.lines.length ? this.track?.metadata?.embeddedPlainLyrics || '' : ''; },
    currentText() { return this.currentLine ? this.currentLine.words.map(word => word.word).join('') : this.lines.length ? '…' : this.plainLyrics || (this.track ? '暂无歌词' : '选择一首歌曲开始播放'); },
    nextText() { const next = this.lines[this.lineIndex + 1]; return next ? next.words.map(word => word.word).join('') : ''; },
    fill() {
      const line = this.currentLine;
      if (!line) return 0;
      const end = Math.max(line.startTime + 1, line.endTime || this.lines[this.lineIndex + 1]?.startTime || line.startTime + 5000);
      return Math.max(0, Math.min(100, (this.playhead * 1000 - line.startTime) / (end - line.startTime) * 100));
    },
    panelStyle() { return { left: this.floating.external ? '0px' : `${this.floating.x}px`, top: this.floating.external ? '0px' : `${this.floating.y}px`, '--floating-font-size': `${this.floating.fontSize}px` }; },
  },
  watch: {
    'floating.open'(open) {
      if (open) { this.error = ''; this.$nextTick(() => { this.clampPosition(); this.startClock(); }); }
      else { this.stopClock(); this.pipWindow?.close(); }
    },
    'state.currentId'() { this.playhead = this.runtime.currentPlaybackTime(); },
    'state.theme'() { this.syncTheme(); },
  },
  mounted() { this.onResize = () => this.clampPosition(); window.addEventListener('resize', this.onResize); },
  beforeDestroy() {
    this.disposed = true; this.stopClock(); this.pipWindow?.close(); window.removeEventListener('resize', this.onResize);
  },
  methods: {
    formatTime(value) { return this.runtime.formatTime(value * 1000); },
    font(delta) { this.floating.fontSize = Math.max(18, Math.min(44, this.floating.fontSize + delta)); saveFloatingLyrics(); },
    translation() { this.floating.translation = !this.floating.translation; saveFloatingLyrics(); },
    toggleLock() {
      this.floating.locked = !this.floating.locked;
      if (this.dragState) { const id = this.dragState.id; this.dragState = null; if (this.$refs.panel?.hasPointerCapture(id)) this.$refs.panel.releasePointerCapture(id); }
      saveFloatingLyrics();
    },
    close() { this.floating.open = false; },
    keydown(event) {
      if (playbackSpace(event)) return;
      if (event.defaultPrevented || event.isComposing || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      if (event.target.closest('input,textarea,[contenteditable="true"]')) return;
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); this.close(); }
    },
    clampPosition() {
      if (this.floating.external || !this.$refs.panel) return;
      const box = this.$refs.panel.getBoundingClientRect();
      this.floating.x = Math.max(8, Math.min(this.floating.x, innerWidth - box.width - 8));
      this.floating.y = Math.max(64, Math.min(this.floating.y, Math.max(64, innerHeight - box.height - 8)));
    },
    startDrag(event) {
      if (this.floating.external || event.button !== 0 || event.target.closest('button,input,a')) return;
      if (event.target.closest('.plain-lines')) return;
      event.preventDefault();
      if (this.floating.locked) return;
      this.dragState = { id: event.pointerId, x: event.clientX, y: event.clientY, left: this.floating.x, top: this.floating.y };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    drag(event) {
      if (!this.dragState || this.dragState.id !== event.pointerId) return;
      this.floating.x = this.dragState.left + event.clientX - this.dragState.x;
      this.floating.y = this.dragState.top + event.clientY - this.dragState.y;
      this.clampPosition();
    },
    endDrag(event) {
      if (!this.dragState || this.dragState.id !== event.pointerId) return;
      this.dragState = null; saveFloatingLyrics();
    },
    stopClock() { if (this.clock) this.clockWindow?.cancelAnimationFrame(this.clock); this.clock = 0; },
    startClock() {
      this.stopClock(); this.clockWindow = this.pipWindow || window;
      const tick = () => { this.playhead = this.runtime.currentPlaybackTime(); if (this.floating.open) this.clock = this.clockWindow.requestAnimationFrame(tick); };
      tick();
    },
    syncTheme() {
      if (!this.pipWindow) return;
      this.pipWindow.document.documentElement.setAttribute('data-theme', document.documentElement.getAttribute('data-theme') || 'light');
    },
    async pin() {
      if (!this.pipSupported || this.pinPending) return;
      this.pinPending = true; this.error = '';
      try {
        // Keep the request directly in the click's transient activation.
        const target = await window.documentPictureInPicture.requestWindow({ width: 560, height: 176, disallowReturnToOpener: true });
        if (!this.floating.open || this.disposed) { target.close(); return; }
        this.pipWindow = target;
        target.document.title = 'Simple Music · 悬浮歌词';
        for (const element of document.querySelectorAll('style,link[rel="stylesheet"]')) {
          const clone = element.cloneNode(true);
          if (element.tagName === 'LINK') clone.href = element.href;
          target.document.head.appendChild(clone);
        }
        target.document.body.style.margin = '0';
        target.document.body.style.overflow = 'hidden';
        target.document.body.appendChild(this.$refs.panel);
        target.addEventListener('keydown', this.keydown, true);
        target.addEventListener('keyup', playbackSpace, true);
        this.floating.external = true; this.syncTheme(); this.startClock();
        target.addEventListener('pagehide', () => {
          if (this.pipWindow !== target) return;
          target.removeEventListener('keydown', this.keydown, true);
          target.removeEventListener('keyup', playbackSpace, true);
          this.$refs.anchor?.appendChild(this.$refs.panel);
          this.pipWindow = null; this.floating.external = false;
          if (!this.keepInline) this.floating.open = false;
          this.keepInline = false;
          if (this.floating.open) this.$nextTick(() => { this.clampPosition(); this.startClock(); });
          else this.stopClock();
        }, { once: true });
      } catch { this.error = '浏览器未允许打开置顶窗口，可继续使用页内悬浮歌词。'; }
      finally { this.pinPending = false; }
    },
    returnToPage() { this.keepInline = true; this.pipWindow?.close(); },
  },
};
</script>

<style lang="scss" scoped>
@use '../salt/ui/tokens' as salt;
.floating-lyrics-anchor { display: contents; }
.floating-lyrics { position:fixed;z-index:360;width:min(520px,calc(100vw - 32px));padding:0 16px;border:1px solid transparent;border-radius:20px;background:#191b23a6;backdrop-filter:blur(20px);color:#fff;box-shadow:0 8px 36px #0002;font-family:system-ui,sans-serif;cursor:move;transition:background 160ms,border-color 160ms; }
.floating-heading,.floating-footer,.floating-actions { display:flex;align-items:center;gap:5px; }
.floating-heading { position:absolute;left:12px;right:12px;top:5px;justify-content:flex-end;user-select:none; }
.floating-heading,.floating-footer { opacity:0;pointer-events:none;transition:opacity 140ms; }
.floating-lyrics:hover .floating-heading,.floating-lyrics:hover .floating-footer,.floating-lyrics:has(:focus-visible) .floating-heading,.floating-lyrics:has(:focus-visible) .floating-footer { opacity:1;pointer-events:auto; }
.floating-lyrics:hover,.floating-lyrics:has(:focus-visible) { background:#191b23ed;border-color:#ffffff30; }
.floating-actions { flex-shrink:0; }
.floating-lyrics button { color:inherit;background:transparent;border:0;border-radius:8px;min-width:32px;height:32px;padding:3px 6px;font-size:14px;cursor:pointer; }
.floating-lyrics button:hover,.floating-lyrics button[aria-pressed="true"] { background:#ffffff24; }
.floating-lyrics button:disabled { opacity:.35;cursor:default; }
.floating-lyrics button:focus-visible { outline:none;box-shadow:inset 0 -2px #9ddac8; }
.floating-lines { box-sizing:border-box;text-align:center;min-height:168px;display:flex;flex-direction:column;justify-content:center;gap:8px;padding:24px 0 28px; }
.floating-heading,.floating-lines:not(.plain-lines) { touch-action:none; }
.floating-lines p { margin:0;overflow-wrap:anywhere;line-height:1.4; }
.floating-current { font-size:var(--floating-font-size);font-weight:700;background:linear-gradient(90deg,#aff5df var(--lyric-fill),#fff var(--lyric-fill));background-clip:text;-webkit-background-clip:text;color:transparent; }
.floating-translation,.floating-next { font-size:calc(var(--floating-font-size)*.55);opacity:.65; }
.floating-footer { position:absolute;bottom:4px;left:16px;right:16px;font-size:11px; }
.plain-lines { display:block;max-height:240px;overflow:auto;touch-action:pan-y;cursor:auto;scrollbar-width:thin; }
.floating-current.is-plain { white-space:pre-wrap;background:none;color:#fff;font-size:calc(var(--floating-font-size)*.75); }
.floating-title { flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;opacity:.6; }
.floating-error { font-size:11px;line-height:1.5;color:#ffd6a0; }
.floating-lyrics.external { position:relative;width:100%;height:100dvh;border:0;border-radius:0;box-shadow:none;box-sizing:border-box;cursor:default;background:#191b23; }
.external .floating-lines { box-sizing:border-box;min-height:100%;max-height:100%;overflow:auto;padding:24px 0; }
@media(hover:none) { .floating-heading,.floating-footer { opacity:1;pointer-events:auto; } }
@media(max-width:420px) { .floating-lyrics { padding:0 10px; }.floating-actions{gap:1px}.floating-actions button{min-width:29px;padding:2px;font-size:12px;} }
// Keep controls and material usable after the panel moves to a PiP document.
.floating-lyrics.salt-floating {
  @include salt.salt-ui-tokens;
  --salt-sub-surface-fallback: light-dark(#f9f9f9, #2c2c2c);
  --salt-sub-surface-blur: 60px;
  --salt-sub-surface-color: light-dark(rgb(252 252 252 / 0), rgb(44 44 44 / .15));
  --salt-sub-surface-luminosity: light-dark(rgb(252 252 252 / .85), rgb(44 44 44 / .96));
  color-scheme: light; color: var(--salt-text); background: transparent;
  border-color: var(--salt-stroke); border-radius: var(--salt-radius-large);
  isolation: isolate; backdrop-filter: none;
  --floating-highlight: var(--salt-accent); --floating-original: var(--salt-text);
  &[data-floating-material='mica'] { --salt-sub-surface-blur:240px; --salt-sub-surface-color:light-dark(rgb(243 243 243 / .5),rgb(32 32 32 / .8)); --salt-sub-surface-luminosity:light-dark(#f3f3f3,#202020); }
  &[data-floating-material='none'] ::v-deep(.salt-material-surface) { background:var(--salt-popup); }
  &[data-floating-material='none'] ::v-deep(.salt-surface-backdrop) { display:none; }
}
[data-theme='dark'] .floating-lyrics.salt-floating { @include salt.salt-ui-dark-tokens; color-scheme:dark; }
.floating-lyrics.apple-floating {
  --salt-text:var(--color-text); --salt-accent:var(--color-primary);
  --salt-hover-overlay:color-mix(in srgb,var(--color-text) 8%,transparent);
  --salt-sub-surface-fallback:color-mix(in srgb,var(--color-body-bg) 94%,transparent);
  --salt-sub-surface-color:transparent; --salt-sub-surface-luminosity:var(--color-body-bg);
  --floating-highlight:var(--color-primary); --floating-original:var(--color-text);
  color:var(--color-text); background:transparent; border-color:color-mix(in srgb,var(--color-text) 12%,transparent);
  border-radius:12px; isolation:isolate; backdrop-filter:none;
}
.floating-lyrics.salt-floating:hover,.floating-lyrics.salt-floating:has(:focus-visible),
.floating-lyrics.apple-floating:hover,.floating-lyrics.apple-floating:has(:focus-visible) { background:transparent; }
.floating-lyrics.salt-floating .floating-current,.floating-lyrics.apple-floating .floating-current {
  background-image:linear-gradient(90deg,var(--floating-highlight) var(--lyric-fill),var(--floating-original) var(--lyric-fill));
}
.floating-lyrics.salt-floating .floating-current.is-plain,.floating-lyrics.apple-floating .floating-current.is-plain { background:none;color:var(--floating-original); }
.floating-lyrics .salt-ui-button { border-radius:999px;min-height:32px;height:32px;padding:0; }
.floating-lyrics .salt-ui-button:hover,.floating-lyrics .salt-ui-button[aria-pressed='true'] { background:var(--salt-hover-overlay); }
.floating-lyrics .salt-ui-button:focus-visible { box-shadow:inset 0 -2px var(--salt-accent); }
.floating-lyrics.salt-floating.external,.floating-lyrics.apple-floating.external { border-radius:0; }
</style>
